/**
 * Hook: useAIConsent
 * 
 * Gestiona el consentimiento del usuario para el uso de IA financiera.
 * 
 * ── Dónde se guarda ──
 * Tabla: public.consent_records
 * consent_type = 'ai_financial_assistant'
 * consent_version = AI_ASSISTANT_CONSENT_VERSION (src/constants/legalSuite.ts)
 * 
 * ── Cómo revocar manualmente (pruebas) ──
 * UPDATE consent_records
 *   SET accepted = false, updated_at = now()
 * WHERE user_id = '<UUID>' AND consent_type = 'ai_financial_assistant';
 */
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { AI_ASSISTANT_CONSENT_VERSION } from '@/constants/legalSuite';

export function useAIConsent() {
  const { user } = useAuth();
  const [hasConsent, setHasConsent] = useState<boolean | null>(null); // null = loading
  const [isLoading, setIsLoading] = useState(true);

  // Load consent status
  useEffect(() => {
    if (!user?.id) {
      setHasConsent(null);
      setIsLoading(false);
      return;
    }

    const load = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('consent_records')
          .select('accepted')
          .eq('user_id', user.id)
          .eq('consent_type', 'ai_financial_assistant')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) throw error;
        setHasConsent(data?.accepted === true);
      } catch (e) {
        console.error('Error loading AI consent:', e);
        setHasConsent(false);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [user?.id]);

  const grantConsent = useCallback(async () => {
    if (!user?.id) return false;
    try {
      const { error } = await supabase.from('consent_records').insert({
        user_id: user.id,
        consent_type: 'ai_financial_assistant',
        consent_version: AI_ASSISTANT_CONSENT_VERSION,
        accepted: true,
        user_agent: navigator.userAgent,
      });
      if (error) throw error;
      setHasConsent(true);
      return true;
    } catch (e) {
      console.error('Error granting AI consent:', e);
      return false;
    }
  }, [user?.id]);

  const revokeConsent = useCallback(async () => {
    if (!user?.id) return false;
    try {
      // Insert a new record with accepted = false (revocation)
      const { error } = await supabase.from('consent_records').insert({
        user_id: user.id,
        consent_type: 'ai_financial_assistant',
        consent_version: AI_ASSISTANT_CONSENT_VERSION,
        accepted: false,
        user_agent: navigator.userAgent,
      });
      if (error) throw error;
      setHasConsent(false);
      return true;
    } catch (e) {
      console.error('Error revoking AI consent:', e);
      return false;
    }
  }, [user?.id]);

  return { hasConsent, isLoading, grantConsent, revokeConsent };
}
